'use client';

import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { keys } from '@sola/contentful-model/keys.ts';
import type { DisplayPurposeProps } from '@sola/schema/dotcom/shared/display.ts';
import { IconNamesSafe } from '@sola/schema/lib/icons/icon-names.ts';
import {
  type ComponentBaseProps,
  type FlexibleContentProps,
} from '@sola/schema/lib/schema.ts';

type MyAlertContentSlots = {
  alertContent?: FlexibleContentProps;
};

type MyAlertProps = ComponentBaseProps<MyAlertContentSlots>;

type MyAlertVariant = NonNullable<DisplayPurposeProps['variants']>;

const defaultSettings = {
  variant: 'neutral',
  noIcon: true,
  dismissible: false,
} satisfies {
  variant: MyAlertVariant;
  noIcon: boolean;
  dismissible: boolean;
};

const getAction = (primaryCTA: FlexibleContentProps['primaryCTA']) => {
  if (!primaryCTA?.link) {
    return;
  }

  const iconName = primaryCTA.icon
    ? IconNamesSafe.parse(primaryCTA.icon)
    : undefined;
  const target = primaryCTA.target || undefined;

  return (
    <Link
      download={primaryCTA.isDownload}
      href={primaryCTA.link}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      title={primaryCTA.title}
    >
      {iconName && <ArkIcon size="28" iconName={iconName} />}
      {primaryCTA.title}
    </Link>
  );
};

export const MyAlert: React.FC<MyAlertProps> = ({ settings, contentSlots }) => {
  const { title, body, primaryCTA } = contentSlots?.alertContent ?? {};
  const { variant, noIcon, dismissible } =
    settings?.type === keys.accountComponents.myAlert
      ? settings
      : defaultSettings;

  return (
    <Alert
      className="my-comfy-xl"
      variant={variant}
      title={title}
      message={body}
      noIcon={noIcon}
      dismissible={dismissible}
      action={getAction(primaryCTA)}
    />
  );
};
